/**
 * src/components/Financial/RetrogradeCalendarSection.jsx
 * 【完全修正版：ダークモード背景対応・星座カラー設定反映・水星逆行UI最適化】
 */

import React, { useState, useEffect } from "react";
import * as Astro from "../../astronomy.js";
import {
  PLANET_SYMBOLS,
  DEFAULT_PLANET_COLORS,
  DEFAULT_SIGN_COLORS,
} from "../../utils/constants.js";
import { styles as commonStyles } from "../../utils/styles.js";
import {
  Calendar,
  BarChart3,
  MoveRight,
  Zap,
  Clock,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

export default function RetrogradeCalendarSection(props) {
  const planetColors = props.planetColors || DEFAULT_PLANET_COLORS;
  const signColors = props.signColors || DEFAULT_SIGN_COLORS;

  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200,
  );
  useEffect(function () {
    const handleResize = function () {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return function () {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  const isMobile = windowWidth <= 768;

  // --- 1. ダークモード判定とテーマ定義 ---
  const isDark = (function () {
    const p = props.isDarkMode;
    if (p === true || p === "true" || p === "dark") return true;
    if (typeof document !== "undefined") {
      var el = document.documentElement;
      var bd = document.body;
      if (
        el.classList.contains("dark") ||
        bd.classList.contains("dark") ||
        el.getAttribute("data-theme") === "dark"
      )
        return true;
    }
    return false;
  })();

  const theme = {
    cardBg: isDark ? "#1c1f26" : "#ffffff",
    subBg: isDark ? "rgba(255, 255, 255, 0.03)" : "#f8fafc",
    border: isDark ? "#2d313d" : "#e2e8f0",
    text: isDark ? "#ffffff" : "#1e293b",
    subText: isDark ? "#9ca3af" : "#64748b",
  };

  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);

  const planets = [
    "Mercury",
    "Venus",
    "Mars",
    "Jupiter",
    "Saturn",
    "Uranus",
    "Neptune",
    "Pluto",
  ];
  const months = [
    "1月",
    "2月",
    "3月",
    "4月",
    "5月",
    "6月",
    "7月",
    "8月",
    "9月",
    "10月",
    "11月",
    "12月",
  ];

  useEffect(
    function () {
      const rxData = Astro.getYearlyRetrogrades(year);
      const allDetails = Astro.getAllPlanetsRetroDetails(year);
      setData({ rx: rxData, details: allDetails });
    },
    [year],
  );

  if (data === null)
    return (
      <div
        style={{ padding: "40px", textAlign: "center", color: theme.subText }}
      >
        計算中...
      </div>
    );

  const getMonthPosition = function (date) {
    const month = date.getMonth();
    const day = date.getDate();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return (month * 100) / 12 + (day / daysInMonth) * (100 / 12);
  };

  /**
   * 星座バッジの描画（アプリ設定 signColors を優先反映）
   */
  const renderSignBadge = function (signSymbol) {
    // 記号から異体字セレクタを除去した純粋な記号でも検索できるように正規化
    const pureSymbol = signSymbol.replace(/[\uFE00-\uFE0F]/g, "");
    const color = signColors[signSymbol] || signColors[pureSymbol] || "#8b5cf6";

    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: "24px",
          height: "24px",
          fontSize: "18px",
          color: color, // 設定された色を適用
          fontWeight: "bold",
        }}
      >
        {pureSymbol + "\uFE0E"}
      </span>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      {/* 1. 年間ガントチャート */}
      <div
        style={{
          background: theme.cardBg,
          borderRadius: "15px",
          border: "1px solid " + theme.border,
          padding: "25px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              fontWeight: "bold",
              fontSize: "16px",
              color: theme.text,
            }}
          >
            <Calendar size={20} color="rgb(147, 39, 143)" />
            <span>{year}年 惑星逆行カレンダー</span>
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={function () {
                setYear(year - 1);
              }}
              style={Object.assign({}, commonStyles.dateLapseBtn, {
                background: theme.subBg,
                border: "1px solid " + theme.border,
                color: theme.text,
              })}
            >
              前年
            </button>
            <button
              onClick={function () {
                setYear(year + 1);
              }}
              style={Object.assign({}, commonStyles.dateLapseBtn, {
                background: theme.subBg,
                border: "1px solid " + theme.border,
                color: theme.text,
              })}
            >
              次年
            </button>
          </div>
        </div>

        <div
          style={{
            position: "relative",
            background: isDark ? "rgba(0,0,0,0.2)" : "#f8fafc",
            borderRadius: "12px",
            padding: "20px 15px",
            overflowX: "auto",
            border: "1px solid " + theme.border,
          }}
        >
          <div style={{ minWidth: "800px" }}>
            <div
              style={{
                display: "flex",
                borderBottom: "1px solid " + theme.border,
                paddingBottom: "10px",
                marginBottom: "10px",
              }}
            >
              <div style={{ width: "100px" }}></div>
              {months.map(function (m) {
                return (
                  <div
                    key={m}
                    style={{
                      flex: 1,
                      fontSize: "10px",
                      color: theme.subText,
                      textAlign: "center",
                      fontWeight: "900",
                    }}
                  >
                    {m}
                  </div>
                );
              })}
            </div>
            {planets.map(function (p) {
              const color = planetColors[p] || theme.text;
              return (
                <div
                  key={p}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    height: "36px",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      width: "100px",
                      fontSize: "11px",
                      fontWeight: "bold",
                      color: theme.text,
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span style={{ color: color, fontSize: "18px" }}>
                      {PLANET_SYMBOLS[p]}
                    </span>
                    {p}
                  </div>
                  <div
                    style={{
                      flex: 1,
                      height: "10px",
                      background: isDark ? "rgba(255,255,255,0.05)" : "#edf2f7",
                      borderRadius: "5px",
                      position: "relative",
                    }}
                  >
                    {(data.rx[p] || []).map(function (range, idx) {
                      const left = getMonthPosition(range.start);
                      const right = getMonthPosition(range.end);
                      return (
                        <div
                          key={idx}
                          style={{
                            position: "absolute",
                            left: left + "%",
                            width: right - left + "%",
                            height: "100%",
                            background: color,
                            borderRadius: "5px",
                            opacity: 0.8,
                            boxShadow: "0 0 12px " + color + "66",
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* 2. 統計セクション */}
      <div
        style={{
          background: theme.cardBg,
          borderRadius: "15px",
          border: "1px solid " + theme.border,
          padding: "25px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginBottom: "20px",
            fontWeight: "bold",
            fontSize: "16px",
            color: theme.text,
          }}
        >
          <BarChart3 size={20} color="rgb(147, 39, 143)" />{" "}
          <span>{year}年 逆行統計</span>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
            gap: "12px",
          }}
        >
          {planets.map(function (p) {
            let count = 0,
              totalDays = 0;
            if (data.rx[p]) {
              count = data.rx[p].length;
              totalDays = data.rx[p].reduce(function (acc, cur) {
                return acc + cur.durationDays;
              }, 0);
            }
            const color = planetColors[p] || theme.text;
            return (
              <div
                key={p}
                style={{
                  background: theme.subBg,
                  padding: "20px 15px",
                  borderRadius: "15px",
                  textAlign: "center",
                  border: "1px solid " + theme.border,
                }}
              >
                <div
                  style={{
                    fontSize: "28px",
                    color: color,
                    marginBottom: "5px",
                  }}
                >
                  {PLANET_SYMBOLS[p]}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: theme.subText,
                    fontWeight: "bold",
                  }}
                >
                  {p}
                </div>
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: "900",
                    color: theme.text,
                    margin: "10px 0",
                  }}
                >
                  {count}{" "}
                  <span style={{ fontSize: "12px", color: theme.subText }}>
                    回
                  </span>
                </div>
                <div
                  style={{ fontSize: "11px", color: color, fontWeight: "bold" }}
                >
                  計 {totalDays}日間
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 3. 水星逆行 詳細分析 */}
      <div
        style={{
          background: theme.cardBg,
          borderRadius: "15px",
          border: "1px solid " + theme.border,
          padding: "25px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginBottom: "25px",
            fontWeight: "bold",
            fontSize: "16px",
            color: theme.text,
          }}
        >
          <Zap size={20} color="#f1c40f" />{" "}
          <span>逆行 詳細分析 (Shadow & Retrograde)</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {data.details.map(function (item, idx) {
            const p = item.planet;
            const color = planetColors[p] || theme.text;
            return (
              <div
                key={idx}
                style={{
                  background: theme.subBg,
                  borderRadius: "16px",
                  border: "1px solid " + theme.border,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    background: isDark
                      ? "rgba(255, 255, 255, 0.05)"
                      : "#f8fafc",
                    padding: "15px 20px",
                    borderBottom: "1px solid " + theme.border,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      fontWeight: "900",
                      color: isDark ? "#60a5fa" : "#2c2d96",
                    }}
                  >
                    <span style={{ color: color, fontSize: "18px" }}>
                      {PLANET_SYMBOLS[p]}
                    </span>
                    <span>{p}</span>
                    <span
                      style={{
                        fontSize: "10px",
                        color: theme.subText,
                        marginLeft: "5px",
                        fontWeight: "normal",
                      }}
                    >
                      PHASE {idx + 1}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: theme.subText,
                      fontWeight: "bold",
                    }}
                  >
                    逆行期間: {item.durationDays}日間
                  </div>
                </div>

                <div
                  style={{
                    padding: "20px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "15px",
                  }}
                >
                  {/* A. プレ・シャドウ (垂直スタック対応) */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: isMobile ? "column" : "row",
                      alignItems: isMobile ? "flex-start" : "center",
                      gap: isMobile ? "6px" : "15px",
                    }}
                  >
                    <div
                      style={{
                        width: isMobile ? "100%" : "120px",
                        fontSize: "11px",
                        fontWeight: "bold",
                        color: theme.subText,
                      }}
                    >
                      プレ・シャドウ
                    </div>
                    <div
                      style={{
                        flex: 1,
                        width: isMobile ? "100%" : "auto",
                        padding: "10px 15px",
                        background: theme.cardBg,
                        borderRadius: "10px",
                        fontSize: "13px",
                        color: theme.text,
                        fontWeight: "bold",
                        border: "1px solid " + theme.border,
                        boxSizing: "border-box",
                      }}
                    >
                      {item.preShadow.start.toLocaleDateString("ja-JP")} 〜{" "}
                      {item.preShadow.end.toLocaleDateString("ja-JP")}
                    </div>
                  </div>

                  {/* B. 逆行期間 (垂直スタック対応) */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: isMobile ? "column" : "row",
                      alignItems: isMobile ? "flex-start" : "center",
                      gap: isMobile ? "6px" : "15px",
                    }}
                  >
                    <div
                      style={{
                        width: isMobile ? "100%" : "120px",
                        fontSize: "11px",
                        fontWeight: "bold",
                        color: theme.subText,
                      }}
                    >
                      逆行期間
                    </div>
                    <div
                      style={{
                        flex: 1,
                        width: isMobile ? "100%" : "auto",
                        padding: "10px 15px",
                        background: theme.cardBg,
                        borderRadius: "10px",
                        fontSize: "13px",
                        color: "#e74c3c",
                        fontWeight: "bold",
                        border: "1px solid " + theme.border,
                        boxSizing: "border-box",
                      }}
                    >
                      {item.start.toLocaleDateString("ja-JP")} 〜{" "}
                      {item.end.toLocaleDateString("ja-JP")}
                    </div>
                  </div>

                  {/* C. ポスト・シャドウ (垂直スタック対応) */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: isMobile ? "column" : "row",
                      alignItems: isMobile ? "flex-start" : "center",
                      gap: isMobile ? "6px" : "15px",
                    }}
                  >
                    <div
                      style={{
                        width: isMobile ? "100%" : "120px",
                        fontSize: "11px",
                        fontWeight: "bold",
                        color: theme.subText,
                      }}
                    >
                      ポスト・シャドウ
                    </div>
                    <div
                      style={{
                        flex: 1,
                        width: isMobile ? "100%" : "auto",
                        padding: "10px 15px",
                        background: theme.cardBg,
                        borderRadius: "10px",
                        fontSize: "13px",
                        color: theme.text,
                        fontWeight: "bold",
                        border: "1px solid " + theme.border,
                        boxSizing: "border-box",
                      }}
                    >
                      {item.postShadow.start.toLocaleDateString("ja-JP")} 〜{" "}
                      {item.postShadow.end.toLocaleDateString("ja-JP")}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
